import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Building2, CheckCircle2, XCircle, AlertCircle, ExternalLink,
  Loader2, Globe, Mail, Users, Shield, Clock, FileText, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTwoFactorPrompt, callWith2FA } from '@/components/TwoFactorPrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'auto_flagged', label: 'Flagged' },
  { value: 'info_requested', label: 'Info Requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

function ScoreBadge({ score }) {
  let cls = 'bg-red-500/20 text-red-400 border-red-500/40';
  if (score >= 75) cls = 'bg-green-500/20 text-green-400 border-green-500/40';
  else if (score >= 50) cls = 'bg-[#00F0FF]/20 text-[#00F0FF] border-[#00F0FF]/40';
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-bold border ${cls}`}
      data-testid="partner-score-badge"
    >
      {score}/100
    </span>
  );
}

function RecBadge({ rec }) {
  const map = {
    auto_approve: 'bg-green-500/20 text-green-400 border-green-500/40',
    review: 'bg-[#00F0FF]/20 text-[#00F0FF] border-[#00F0FF]/40',
    auto_reject: 'bg-red-500/20 text-red-400 border-red-500/40',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs border ${map[rec] || ''}`}>
      {rec}
    </span>
  );
}

function ApplicationDrawer({ application, onClose, onAction }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(null);
  const { promptFor2FA, TwoFactorPromptModal } = useTwoFactorPrompt();

  if (!application) return null;
  const v = application.verification || {};
  const checks = v.checks || {};

  const act = async (kind) => {
    if (kind === 'reject' && !notes.trim()) {
      toast.error('Please add a rejection reason in the notes field.');
      return;
    }
    setBusy(kind);
    try {
      const token = localStorage.getItem('token');
      await callWith2FA(
        (extraHeaders = {}) => axios.post(
          `${API_URL}/api/admin/partner-applications/${application.application_id}/${kind}`,
          { notes },
          { headers: { Authorization: `Bearer ${token}`, ...extraHeaders } }
        ),
        promptFor2FA
      );
      toast.success(`Application ${kind === 'request-info' ? 'flagged for info' : kind + 'd'}.`);
      onAction();
      onClose();
    } catch (e) {
      if (e.cancelled) return;
      toast.error(e.response?.data?.detail || `Failed to ${kind}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-stretch justify-end"
      onClick={onClose}
      data-testid="partner-app-drawer"
    >
      <div
        className="w-full max-w-2xl bg-neutral-950 border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-neutral-950/95 backdrop-blur border-b border-white/10 p-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-[#00F0FF]" />
            <div>
              <div className="font-bold text-lg">{application.company_name}</div>
              <div className="text-xs text-neutral-400">{application.application_id}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-lg"
            data-testid="close-drawer-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Verification scorecard */}
          <section className="bg-neutral-900/60 border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-semibold uppercase text-[#00F0FF]">
                <Shield className="w-4 h-4" /> Automated verification
              </div>
              <div className="flex items-center gap-2">
                <ScoreBadge score={v.score ?? 0} />
                <RecBadge rec={v.recommendation} />
              </div>
            </div>
            {v.risk_flags?.length > 0 && (
              <div className="mb-3">
                <div className="text-xs text-neutral-400 uppercase mb-1">Risk flags</div>
                <div className="flex flex-wrap gap-1.5">
                  {v.risk_flags.map((f) => (
                    <span
                      key={f}
                      className="text-xs bg-red-500/10 text-red-400 border border-red-500/30 px-2 py-0.5 rounded"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <CheckRow label="Website reachable" ok={checks.website?.reachable} detail={checks.website?.url} />
              <CheckRow label="DNS resolves" ok={checks.dns?.resolves} detail={checks.dns?.ip} />
              <CheckRow label="Company name on website" ok={checks.website?.company_mentioned} detail={checks.website?.title} />
              <CheckRow
                label="Email domain matches website"
                ok={checks.email_domain?.match}
                detail={`${checks.email_domain?.email_domain || ''} vs ${checks.email_domain?.website_domain || ''}`}
              />
              <CheckRow label="Social 1 reachable" ok={checks.social_1?.reachable} detail={checks.social_1?.og_title} />
              <CheckRow label="Social 1 handle match" ok={checks.social_1?.handle_match} />
              <CheckRow label="Social 2 reachable" ok={checks.social_2?.reachable} detail={checks.social_2?.og_title} />
              <CheckRow label="Social 2 handle match" ok={checks.social_2?.handle_match} />
            </div>
          </section>

          {/* Company info */}
          <section className="space-y-2 text-sm">
            <InfoRow icon={Globe} label="Website" value={<a href={application.website} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline flex items-center gap-1">{application.website} <ExternalLink className="w-3 h-3" /></a>} />
            <InfoRow icon={Mail} label="Business email" value={application.business_email} />
            <InfoRow icon={Users} label="Contact" value={application.contact_name} />
            <InfoRow icon={FileText} label="Subscription type" value={application.subscription_type} />
            <InfoRow icon={ExternalLink} label="Social 1" value={<a href={application.social_1_url} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">{application.social_1_url}</a>} />
            <InfoRow icon={ExternalLink} label="Social 2" value={<a href={application.social_2_url} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">{application.social_2_url}</a>} />
            <InfoRow icon={Clock} label="Submitted" value={new Date(application.submitted_at).toLocaleString()} />
          </section>

          <section>
            <div className="text-xs text-neutral-400 uppercase mb-1">Description</div>
            <div className="text-sm whitespace-pre-wrap bg-neutral-900/60 border border-white/10 rounded-lg p-3">
              {application.description}
            </div>
          </section>

          {application.supporting_notes && (
            <section>
              <div className="text-xs text-neutral-400 uppercase mb-1">Supporting notes</div>
              <div className="text-sm whitespace-pre-wrap bg-neutral-900/60 border border-white/10 rounded-lg p-3">
                {application.supporting_notes}
              </div>
            </section>
          )}

          {['approved', 'rejected', 'info_requested'].includes(application.status) ? (
            <div className="bg-neutral-900/60 border border-white/10 rounded-xl p-4 text-sm">
              <div className="text-neutral-400 uppercase text-xs mb-1">Decision</div>
              <div className="font-semibold capitalize">{application.status.replace('_', ' ')}</div>
              {application.reviewed_by && (
                <div className="text-xs text-neutral-400 mt-1">By {application.reviewed_by} on {new Date(application.reviewed_at).toLocaleString()}</div>
              )}
              {application.review_notes && (
                <div className="text-sm mt-2 whitespace-pre-wrap">{application.review_notes}</div>
              )}
            </div>
          ) : (
            <section className="space-y-3">
              <Textarea
                placeholder="Review notes (required for reject)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-neutral-900/60 border-white/10 text-white min-h-[96px]"
                data-testid="partner-review-notes"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => act('approve')}
                  disabled={!!busy}
                  className="bg-green-500 hover:bg-green-600 text-black font-semibold"
                  data-testid="approve-partner-btn"
                >
                  {busy === 'approve' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Approve
                </Button>
                <Button
                  onClick={() => act('request-info')}
                  disabled={!!busy}
                  variant="outline"
                  className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                  data-testid="request-info-partner-btn"
                >
                  Request more info
                </Button>
                <Button
                  onClick={() => act('reject')}
                  disabled={!!busy}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="reject-partner-btn"
                >
                  {busy === 'reject' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Reject
                </Button>
              </div>
            </section>
          )}
        </div>
      </div>
      <TwoFactorPromptModal />
    </div>
  );
}

function CheckRow({ label, ok, detail }) {
  return (
    <div className="flex items-start gap-2">
      {ok ? <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className="text-white">{label}</div>
        {detail && <div className="text-xs text-neutral-500 truncate">{detail}</div>}
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-neutral-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-neutral-400 uppercase">{label}</div>
        <div className="text-white text-sm break-words">{value}</div>
      </div>
    </div>
  );
}

export default function AdminPartnerApplications() {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/partner-applications`, {
        params: { status },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load partner applications');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#00F0FF]" />
            Partner Applications
          </h1>
          <p className="text-neutral-400 mt-1">
            Verified business sellers applying to sell subscriptions on the marketplace.
            Review the automated verification scorecard, then approve / reject / request info.
          </p>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="partner-status-filter">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.value}
              variant={status === f.value ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setStatus(f.value)}
              className={status === f.value ? 'bg-white text-black' : 'text-neutral-400 border border-white/10'}
            >
              {f.label}
              {f.value === 'pending' && data?.pending_count > 0 && (
                <span className="ml-2 bg-[#00F0FF] text-black px-1.5 py-0.5 rounded text-xs">{data.pending_count}</span>
              )}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
          </div>
        ) : !data?.applications?.length ? (
          <div className="text-center py-20 text-neutral-500">
            <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
            No partner applications in this view.
          </div>
        ) : (
          <div className="bg-neutral-900/40 border border-white/10 rounded-xl overflow-hidden" data-testid="partner-apps-table">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900/80 text-xs text-neutral-400 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Company</th>
                  <th className="px-4 py-3 text-left">Score</th>
                  <th className="px-4 py-3 text-left">Recommendation</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Submitted</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.applications.map((a) => {
                  const v = a.verification || {};
                  return (
                    <tr key={a.application_id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3">
                        <div className="font-medium">{a.company_name}</div>
                        <div className="text-xs text-neutral-500">{a.business_email}</div>
                      </td>
                      <td className="px-4 py-3"><ScoreBadge score={v.score ?? 0} /></td>
                      <td className="px-4 py-3"><RecBadge rec={v.recommendation} /></td>
                      <td className="px-4 py-3">
                        <span className="capitalize">{(a.status || '').replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-400">
                        {new Date(a.submitted_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelected(a)}
                          className="text-[#00F0FF] hover:bg-[#00F0FF]/10"
                          data-testid={`review-partner-${a.application_id}`}
                        >
                          Review
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <ApplicationDrawer
          application={selected}
          onClose={() => setSelected(null)}
          onAction={load}
        />
      )}
    </AdminLayout>
  );
}
